/**
 * UpgradePrompt Component
 *
 * Displays a prompt to upgrade subscription when a feature is locked.
 */

import React from "react";
import { Card, Text, Button, Stack, Badge, Group } from "@mantine/core";
import { IconLock, IconArrowUp } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  SubscriptionTier,
  FeatureKey,
  TIER_CONFIG,
  FEATURE_DISPLAY_NAMES,
} from "../../types/featureAccess";
import styles from "./UpgradePrompt.module.css";

interface UpgradePromptProps {
  /** The tier required to access the feature */
  requiredTier: SubscriptionTier;
  /** The feature that is locked */
  feature: FeatureKey;
  /** The user's current tier */
  currentTier: SubscriptionTier;
  /** Whether to show a compact version */
  compact?: boolean;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  requiredTier,
  feature,
  currentTier,
  compact = false,
}) => {
  const navigate = useNavigate();
  const tierConfig = TIER_CONFIG[requiredTier];
  const featureName = FEATURE_DISPLAY_NAMES[feature];
  const tierDisplayName = tierConfig?.displayName ?? requiredTier;

  const handleUpgrade = () => {
    navigate("/admin/settings/subscription");
  };

  if (compact) {
    return (
      <Group gap="xs" className={styles.compactPrompt}>
        <IconLock size={16} />
        <Text size="sm" c="dimmed">
          Upgrade to {tierDisplayName} for access
        </Text>
        <Button size="xs" variant="light" color="teal" onClick={handleUpgrade}>
          Upgrade
        </Button>
      </Group>
    );
  }

  return (
    <Card className={styles.upgradeCard} withBorder p="xl">
      <Stack align="center" gap="md">
        <div className={styles.lockIconWrapper}>
          <IconLock size={48} className={styles.lockIcon} />
        </div>

        <Text size="lg" fw={600} ta="center">
          {featureName} is locked
        </Text>

        <Text c="dimmed" ta="center" size="sm">
          Upgrade to {tierDisplayName} for access
        </Text>

        <Badge color="teal" size="lg" variant="light">
          {tierConfig
            ? `Starting at $${(tierConfig.price / 100).toFixed(0)}/month`
            : "View plans"}
        </Badge>

        <Button
          leftSection={<IconArrowUp size={16} />}
          onClick={handleUpgrade}
          color="teal"
          size="md"
        >
          View Plans
        </Button>
      </Stack>
    </Card>
  );
};

export default UpgradePrompt;
